import { Box, FlexBox, Text } from '@dixa/lib-shared-habla';
import type { Tag } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import { ComponentProps, useEffect, useMemo } from 'react';

import { toHexColor } from 'utils/formatting/string';

type TagContentProps = ComponentProps<typeof FlexBox> & {
  /** The tag. */
  tag: Pick<Tag, 'name' | 'color'>;
  /** The callback for when the color changes. */
  onColorChange?: (color: string) => void;
};

/** Represents a component that renders a tag name with an adjacent color. */
export const TagContent = ({
  tag,
  onColorChange = noop,
  ...props
}: TagContentProps): JSX.Element => {
  const color = useMemo(
    () => tag.color ?? toHexColor(tag.name),
    [tag.color, tag.name],
  );

  useEffect(() => {
    onColorChange(color);
  }, [color, onColorChange]);

  return (
    <FlexBox alignItems="center" gap="200" {...props}>
      <Box
        style={{
          flexShrink: 0,
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      <Box style={{ wordBreak: 'break-word' }}>
        <Text.Paragraph>{tag.name}</Text.Paragraph>
      </Box>
    </FlexBox>
  );
};
